import React, { useState } from "react";
import Modal from "react-modal";
import { useDropzone } from "react-dropzone";
import "cropperjs/dist/cropper.css";
import axios from "axios";
import ProgressBar from "components/ProgressBar";
import { Img } from "components/Img";
import { ErrorMessage } from "formik";
import { showGlobalModal } from "components/GlobalModal";
import { isImageUrl, renderBaseUrlForApi } from "utils";
import { convertImageToWebP } from "utils/convertImageToWebP";

Modal.setAppElement("#root");

function getFileNameFromUrl(url) {
  return url.substring(url?.lastIndexOf('/') + 1);
}

const ImageUpload: React.FC<{
  onChange: (file: object) => void;
  imageValue: {
    orignalImage: String;
    selectedImage: String;
    enhancedImage: String;
  };
  label?: string | any;
  customMessage?: string;
  wantHDOption?: boolean;
  previewNotRequired?: boolean;
  disable?: boolean;
  labelClassNames?: string;
  containerHeight?: string | any; // New prop for flexible height
  maxSize?: number;
  isError?: boolean;
  required?: boolean;
  labledDesign?: boolean;
  placeHolder?: string;
  allow?: string;
}> = ({
  onChange,
  imageValue,
  label,
  customMessage,
  wantHDOption,
  disable,
  maxSize = 5,
  isError,
  required,
  labledDesign,
  placeHolder,
  allow
}) => {

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [loader, setLoader] = useState<boolean>(false);
    const [uploadController, setUploadController] = useState<AbortController | null>(null);

    const IS_ORIGNAL_IMAGE = Boolean(
      imageValue?.selectedImage === imageValue?.orignalImage
    );
    const IS_ENHANCED_IMAGE = Boolean(
      imageValue?.selectedImage === imageValue?.enhancedImage
    );

    const handleImageUpload = async (file: File) => {
      if (!allow && file.type !== "image/jpeg") {
        showGlobalModal("Only JPG/JPEG files are allowed!", "warning");
        return;
      }

      // Create a new AbortController
      const controller = new AbortController();
      setUploadController(uploadController => controller);

      setLoader(loader => true);

      try {
        const webPFile = await convertImageToWebP(file);
        const formData = new FormData();
        formData.append("files", webPFile);
        const response = await axios.post(
          `${renderBaseUrlForApi()}/master/upload/webTempFiles`,
          formData,
          {
            headers: { "Custom-Header": "multipart/form-data" },
            signal: controller.signal, // Attach the signal for cancellation
          }
        );
        onChange({
          orignalImage: response?.data?.data?.[0],
          selectedImage: response?.data?.data?.[0],
          enhancedImage: "",
        });
        setModalIsOpen(modalIsOpen => true);
      } catch (error: any) {
        if (axios.isCancel(error)) {
          console.log("Upload aborted:", error.message);
        } else {
          showGlobalModal(error?.response?.data?.message || "Upload failed!", "error");
        }
      } finally {
        setLoader(loader => false);
      }
    };

    // Function to cancel the request
    const handleCancelUpload = () => {
      if (uploadController) {
        uploadController.abort();
        setUploadController(uploadController => null);
        setLoader(loader => false);
      }
    };

    const handleSelection = (url) => {
      // setSelectedImage(selectedImage => url);
      onChange({
        ...imageValue,
        selectedImage: url,
      });
    };

    const handleModalCancel = () => {
      // setEnhancedImage(enhancedImage => null);
      setModalIsOpen((modalIsOpen) => false);
    };

    const handleSaveEnhancedImage = () => {
      // onChange(selectedImage);
      // setEnhancedImage(null);
      setModalIsOpen(false);
    };

    const onDrop = (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (maxSize && file.size > maxSize * 1024 * 1024) {
          showGlobalModal(`File size exceeds ${maxSize}MB`, "error");
          return;
        }
        handleImageUpload(file);
      }
    };

    const handleEnhanceImage = async () => {
      if (!imageValue?.orignalImage) return;
      try {
        setLoader((loader) => true);
        const response = await axios.post(
          `${renderBaseUrlForApi()}/master/enhance-image`,
          { image_url: imageValue?.orignalImage, preset: "auto_enhance" }
        );
        if (!response?.data?.data?.result_url) {
          showGlobalModal(response?.data?.message, "error");
        } else {
          // setEnhancedImage(enhancedImage => response?.data?.data?.result_url);
          onChange({
            ...imageValue,
            enhancedImage: response?.data?.data?.result_url,
          });
        }
        setLoader((loader) => false);
      } catch (err) {
        setLoader((loader) => false);
        showGlobalModal(err?.data?.message || err?.message, "error");
      }
    };

    const handleDeleteTheUploadedFile = () => {
      onChange({
        orignalImage: "",
        selectedImage: "",
        enhancedImage: "",
      });
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: { "image/*": [], ...(!!allow && { [allow]: [] }) }, // not gif
      multiple: false,
      disabled: disable,
    });

    return (
      <div className="w-full space-y-1">
        <div className="flex items-center justify-between">
          {(!!label && !!labledDesign) && (
            <label
              // className={`font-manropeSB text-[#4a5568] text-[1.8vh] ${labelClassNames || ""}`}
              className={`font-manropeSB text-[#4a5568] text-[1.8vh]`}
            >
              {label} {!!required && <span style={{ color: '#E24B4A' }}>*</span>}
            </label>)}
          <span>
          </span>
        </div>
        <div
          className={`
          relative 
          flex 
          flex-col 
          items-center 
          justify-center
          px-2 
          py-2
          border 
          border-solid 
          ${isError ? "border-red-500" : "border-gray-300"}
          rounded-xl 
          bg-[white]
          overflow-hidden 
          ${isDragActive ? " bg-blue-100" : ""}
        `}
        >
          <input {...getInputProps()} />
          <div className="w-full">
            {!imageValue?.orignalImage ? <div className="flex flex-col justify-center items-center gap-y-2">
              <div className="flex justify-between items-center gap-4 w-full">
                {loader ? <ProgressBar /> :
                  (!!label && <label className="block text-[1.8vh] font-medium text-[#787878]">
                    {placeHolder || label}{" "}
                    {!!customMessage && <span className="text-[1.3vh]">{customMessage || ` JPG, JPEG (Max ${maxSize}MB)`}</span>}
                  </label>)}
                {loader ? <button onClick={handleCancelUpload} type="button"><Img src="/images/new-landing-page/blackCloseButton.svg" alt="blackCloseButton" className="object-contain" /></button> :
                  <button
                    {...getRootProps()}
                    type="button"
                    className={`font-manropeB py-2 px-4 ${!!labledDesign ? "text-purple-primary" : "bg-blue-700 hover:bg-blue-800"} text-white rounded-lg focus:ring-4 focus:ring-blue-300 text-[1.8vh] font-medium`}
                    style={{ color: !!labledDesign ? "#5E4DE0" : '#fff' }}
                  >
                    Upload
                  </button>}
              </div>
              {/* <div className="flex-1 w-full rounded-md">
                    <Img src="/images/ImageEnhancerLabel.svg" />
                </div> */}
            </div> : <div className="flex justify-between items-center gap-x-4">
              <div className="flex gap-x-4 items-center">
                {isImageUrl(imageValue?.selectedImage) ? <Img src={imageValue?.selectedImage as string} alt="image" className="min-w-[5vh] min-h-[5vh] max-w-[5vh] max-h-[5vh] object-contain rounded-full" /> : <span className="text-[3.5vh]">📄</span>}
                <label className="block text-[2vh] font-medium text-gray-700">
                  {getFileNameFromUrl(imageValue?.selectedImage)}
                </label>
              </div>
              <div className="flex items-center gap-x-4">
                {wantHDOption && <button
                  className={"bg-[#5E4DE0] rounded-full p-1"}
                  onClick={e => {
                    e.stopPropagation();
                    if (!disable) {
                      if (!loader) setModalIsOpen((modalIsOpen) => true);
                    }
                  }} type="button"><Img src="/images/new-landing-page/descriptionImage.svg" alt="enhanceImage" className="w-[3vh] h-[3vh] object-contain" /></button>}
                <button disabled={disable} onClick={handleDeleteTheUploadedFile} type="button"><Img src="/images/new-landing-page/delete.svg" alt="delete" className="object-contain w-[3vh] h-[3vh]" /></button>
              </div>
            </div>}
          </div>
        </div>

        {wantHDOption && (
          <Modal
            isOpen={modalIsOpen}
            onRequestClose={handleModalCancel}
            contentLabel="Enhance Image"
            className="fixed inset-0 flex items-center justify-center z-40"
            overlayClassName="fixed inset-0 bg-[black] bg-opacity-50 z-[1001]"
          // style={{zIndex: 1001}}
          >
            <div className="bg-[white] p-4 rounded-lg w-[60%]">
              <div className="flex flex-row justify-between items-stretch gap-4">
                <div className="w-full">
                  <h3 className="text-base font-bold mb-2">Original Image</h3>
                  {!!imageValue?.orignalImage && (
                    <div className="relative h-64 md:h-80">
                      <img
                        src={imageValue?.orignalImage as any}
                        alt="Original"
                        className={`cursor-pointer rounded-md object-contain w-full h-full ${IS_ORIGNAL_IMAGE
                          ? "border-4 border-indigo-600"
                          : "border border-solid border-slate-900"
                          }`}
                        onClick={() => handleSelection(imageValue?.orignalImage)}
                      />
                    </div>
                  )}
                </div>
                <div className="w-full">
                  <h3 className="text-base font-bold mb-2">Enhanced Image</h3>
                  {!!imageValue?.enhancedImage ? (
                    <div className="relative h-64 md:h-80">
                      <img
                        src={imageValue?.enhancedImage as any}
                        alt="Enhanced"
                        className={`cursor-pointer rounded-md object-contain w-full h-full ${IS_ENHANCED_IMAGE
                          ? "border-4 border-indigo-600"
                          : "border border-solid border-slate-900"
                          }`}
                        onClick={() => handleSelection(imageValue?.enhancedImage)}
                      />
                    </div>
                  ) : (
                    <div className="relative h-64 md:h-80 flex items-center justify-center bg-gray-100 border border-solid border-slate-900 rounded-md">
                      <button
                        type="button"
                        className={`absolute top-2 right-2`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!loader) handleEnhanceImage();
                        }}
                        title="Enhance Image"
                      >
                        {!loader ? (
                          <div className="bg-[#5E4DE0] rounded-full p-1"> <Img src="/images/new-landing-page/descriptionImage.svg" alt="enhanceImage" className="w-5 h-5 object-contain" /></div>
                        ) : (
                          <img
                            src={"/images/loader.gif"}
                            className="w-6 h-6"
                            alt="Loading"
                          />
                        )}
                      </button>
                      <p className="text-sm text-gray-500">
                        No enhanced image available
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-4">
                <button
                  type="button"

                  onClick={handleSaveEnhancedImage}
                  style={{ color: '#fff' }}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-center text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 focus:outline-none"
                >
                  Save Image
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    );
  };

export default ImageUpload;
